/**
 * JCE V2 Admin Page
 * 
 * Manages Repair Intents and JCE Rules.
 * Provides CRUD operations for the Job Completeness Engine V2 configuration.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { 
  Settings2, 
  FileText, 
  Plus, 
  Edit2, 
  Trash2, 
  ToggleLeft, 
  ToggleRight,
  Search,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Lightbulb,
  ShieldAlert,
  X,
  Check,
  RefreshCw,
  Database,
  Layers
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// System categories for repair intents
const SYSTEMS = [
  { id: 'brakes', label: 'Brakes', color: 'bg-red-100 text-red-700' },
  { id: 'suspension', label: 'Suspension', color: 'bg-blue-100 text-blue-700' },
  { id: 'tyres', label: 'Tyres', color: 'bg-slate-100 text-slate-700' },
  { id: 'drivetrain', label: 'Drivetrain', color: 'bg-purple-100 text-purple-700' },
  { id: 'engine', label: 'Engine', color: 'bg-orange-100 text-orange-700' },
  { id: 'cooling', label: 'Cooling', color: 'bg-cyan-100 text-cyan-700' },
  { id: 'electrical', label: 'Electrical', color: 'bg-yellow-100 text-yellow-700' },
  { id: 'exhaust', label: 'Exhaust', color: 'bg-gray-100 text-gray-700' },
  { id: 'service', label: 'Service', color: 'bg-green-100 text-green-700' },
  { id: 'general', label: 'General', color: 'bg-slate-100 text-slate-600' },
];

// Confidence levels
const CONFIDENCE_LEVELS = ['high', 'medium', 'low'];

// Suggested addition types
const ADDITION_TYPES = ['labour', 'package', 'fee', 'part', 'service'];

export default function JCEAdmin() {
  const [activeTab, setActiveTab] = useState('intents');
  const [repairIntents, setRepairIntents] = useState([]);
  const [jceRules, setJceRules] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [systemFilter, setSystemFilter] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  
  // Modal states
  const [showCreateIntentModal, setShowCreateIntentModal] = useState(false);
  const [showCreateRuleModal, setShowCreateRuleModal] = useState(false);
  const [editingIntent, setEditingIntent] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [expandedRules, setExpandedRules] = useState(new Set());

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [intentsRes, rulesRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/jce-v2/repair-intents?active_only=${showActiveOnly}${systemFilter ? `&system=${systemFilter}` : ''}`),
        fetch(`${API_URL}/api/admin/jce-v2/rules?active_only=${showActiveOnly}`),
        fetch(`${API_URL}/api/admin/jce-v2/stats`),
      ]);
      
      if (intentsRes.ok) {
        const data = await intentsRes.json();
        setRepairIntents(data.repair_intents || []);
      }
      if (rulesRes.ok) {
        const data = await rulesRes.json();
        setJceRules(data.rules || []);
      }
      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
    } catch (error) {
      console.error('Error fetching JCE data:', error);
      toast.error('Failed to load JCE data');
    } finally {
      setLoading(false);
    }
  }, [showActiveOnly, systemFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ============== REPAIR INTENTS CRUD ==============
  
  const handleCreateIntent = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/jce-v2/repair-intents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create repair intent');
      }
      toast.success('Repair intent created');
      fetchData();
      setShowCreateIntentModal(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateIntent = async (code, data) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/jce-v2/repair-intents/${code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success('Repair intent updated');
      fetchData();
      setEditingIntent(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteIntent = async (code) => {
    if (!window.confirm('Delete or deactivate this repair intent?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/jce-v2/repair-intents/${code}`, {
        method: 'DELETE',
      });
      const result = await res.json();
      if (!res.ok) throw new Error('Failed to delete');
      toast.success(result.action === 'deactivated' ? 'Repair intent deactivated (in use by rules)' : 'Repair intent deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // ============== JCE RULES CRUD ==============

  const handleCreateRule = async (data) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/jce-v2/rules`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to create JCE rule');
      }
      toast.success('JCE rule created');
      fetchData();
      setShowCreateRuleModal(false);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateRule = async (code, data) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/jce-v2/rules/${code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update');
      toast.success('JCE rule updated');
      fetchData();
      setEditingRule(null);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteRule = async (code) => {
    if (!window.confirm('Delete this JCE rule?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/jce-v2/rules/${code}`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('Failed to delete');
      toast.success('JCE rule deleted');
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleRule = async (code) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/jce-v2/rules/${code}/toggle`, {
        method: 'POST',
      });
      const result = await res.json();
      if (!res.ok) throw new Error('Failed to toggle');
      toast.success(`Rule ${result.is_active ? 'activated' : 'deactivated'}`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Seed data
  const handleSeedData = async () => {
    if (!window.confirm('Seed database with initial JCE data? This will not duplicate existing records.')) return;
    try {
      const res = await fetch(`${API_URL}/api/jce-v2/seed`, { method: 'POST' });
      const result = await res.json();
      if (!res.ok) throw new Error('Failed to seed');
      toast.success(`Seeded ${result.repair_intents_seeded} intents and ${result.jce_rules_seeded} rules`);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // Filter items by search
  const filteredIntents = repairIntents.filter(intent => 
    !searchQuery || 
    intent.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    intent.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredRules = jceRules.filter(rule =>
    !searchQuery ||
    rule.code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rule.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rule.trigger?.value?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const toggleRuleExpanded = (code) => {
    setExpandedRules(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const getSystemStyle = (system) => {
    const found = SYSTEMS.find(s => s.id === system);
    return found ? found.color : 'bg-slate-100 text-slate-600';
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="jce-admin-page">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Layers size={24} className="text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">JCE V2 Admin</h1>
              <p className="text-sm text-slate-500">Manage Repair Intents and JCE Rules</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={handleSeedData}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              <Database size={16} />
              Seed Data
            </button>
            <button
              onClick={fetchData}
              disabled={loading}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
        </div>
        
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-4 gap-4 mt-4">
            <div className="bg-blue-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-blue-700">{stats.repair_intents?.active || 0}</div>
              <div className="text-xs text-blue-600">Active Intents</div>
            </div>
            <div className="bg-purple-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-purple-700">{stats.jce_rules?.active || 0}</div>
              <div className="text-xs text-purple-600">Active Rules</div>
            </div>
            <div className="bg-green-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-green-700">{stats.coverage?.coverage_percentage || 0}%</div>
              <div className="text-xs text-green-600">Intent Coverage</div>
            </div>
            <div className="bg-amber-50 rounded-lg p-3">
              <div className="text-2xl font-bold text-amber-700">{stats.coverage?.intents_without_rules || 0}</div>
              <div className="text-xs text-amber-600">Intents Without Rules</div>
            </div>
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-6">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('intents')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'intents'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-600 hover:text-slate-800'
            }`}
          >
            <div className="flex items-center gap-2">
              <FileText size={16} />
              Repair Intents ({filteredIntents.length})
            </div>
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'rules'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-slate-600 hover:text-slate-800'
            }`}
          >
            <div className="flex items-center gap-2">
              <Settings2 size={16} />
              JCE Rules ({filteredRules.length})
            </div>
          </button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white border-b border-slate-200 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border border-slate-200 rounded-lg text-sm w-64 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>
            
            {activeTab === 'intents' && (
              <select
                value={systemFilter}
                onChange={(e) => setSystemFilter(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="">All Systems</option>
                {SYSTEMS.map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>
            )}
            
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input
                type="checkbox"
                checked={showActiveOnly}
                onChange={(e) => setShowActiveOnly(e.target.checked)}
                className="rounded border-slate-300"
              />
              Active only
            </label>
          </div>
          
          <button
            onClick={() => activeTab === 'intents' ? setShowCreateIntentModal(true) : setShowCreateRuleModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            <Plus size={16} />
            {activeTab === 'intents' ? 'New Intent' : 'New Rule'}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw size={24} className="animate-spin text-slate-400" />
          </div>
        ) : activeTab === 'intents' ? (
          <RepairIntentsTable
            intents={filteredIntents}
            onEdit={setEditingIntent}
            onDelete={handleDeleteIntent}
            getSystemStyle={getSystemStyle}
          />
        ) : (
          <JCERulesTable
            rules={filteredRules}
            onEdit={setEditingRule}
            onDelete={handleDeleteRule}
            onToggle={handleToggleRule}
            expandedRules={expandedRules}
            toggleExpanded={toggleRuleExpanded}
            repairIntents={repairIntents}
          />
        )}
      </div>

      {/* Modals */}
      {showCreateIntentModal && (
        <RepairIntentModal
          onClose={() => setShowCreateIntentModal(false)}
          onSave={handleCreateIntent}
          systems={SYSTEMS}
        />
      )}
      
      {editingIntent && (
        <RepairIntentModal
          intent={editingIntent}
          onClose={() => setEditingIntent(null)}
          onSave={(data) => handleUpdateIntent(editingIntent.code, data)}
          systems={SYSTEMS}
        />
      )}
      
      {showCreateRuleModal && (
        <JCERuleModal
          onClose={() => setShowCreateRuleModal(false)}
          onSave={handleCreateRule}
          repairIntents={repairIntents}
        />
      )}
      
      {editingRule && (
        <JCERuleModal
          rule={editingRule}
          onClose={() => setEditingRule(null)}
          onSave={(data) => handleUpdateRule(editingRule.code, data)}
          repairIntents={repairIntents}
        />
      )}
    </div>
  );
}

// ============== REPAIR INTENTS TABLE ==============

function RepairIntentsTable({ intents, onEdit, onDelete, getSystemStyle }) {
  if (intents.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <FileText size={48} className="mx-auto mb-4 opacity-30" />
        <p>No repair intents found</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Code</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Name</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">System</th>
            <th className="text-left px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Status</th>
            <th className="text-right px-4 py-3 text-xs font-semibold text-slate-600 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {intents.map((intent) => (
            <tr key={intent.code} className="hover:bg-slate-50 transition-colors">
              <td className="px-4 py-3">
                <code className="text-sm font-mono bg-slate-100 px-2 py-0.5 rounded">{intent.code}</code>
              </td>
              <td className="px-4 py-3">
                <div className="text-sm font-medium text-slate-800">{intent.name}</div>
                {intent.description && (
                  <div className="text-xs text-slate-500 mt-0.5">{intent.description}</div>
                )}
              </td>
              <td className="px-4 py-3">
                <span className={`text-xs font-medium px-2 py-1 rounded-full ${getSystemStyle(intent.system)}`}>
                  {intent.system || 'general'}
                </span>
              </td>
              <td className="px-4 py-3">
                {intent.is_active ? (
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-green-100 text-green-700">Active</span>
                ) : (
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-500">Inactive</span>
                )}
              </td>
              <td className="px-4 py-3 text-right">
                <div className="flex items-center justify-end gap-2">
                  <button
                    onClick={() => onEdit(intent)}
                    className="p-1.5 hover:bg-slate-100 rounded transition-colors"
                    title="Edit"
                  >
                    <Edit2 size={14} className="text-slate-500" />
                  </button>
                  <button
                    onClick={() => onDelete(intent.code)}
                    className="p-1.5 hover:bg-red-50 rounded transition-colors"
                    title="Delete"
                  >
                    <Trash2 size={14} className="text-red-500" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============== JCE RULES TABLE ==============

function JCERulesTable({ rules, onEdit, onDelete, onToggle, expandedRules, toggleExpanded, repairIntents }) {
  if (rules.length === 0) {
    return (
      <div className="text-center py-12 text-slate-500">
        <Settings2 size={48} className="mx-auto mb-4 opacity-30" />
        <p>No JCE rules found</p>
      </div>
    );
  }

  const getIntentName = (code) => {
    const intent = repairIntents.find(i => i.code === code);
    return intent?.name || code;
  };

  return (
    <div className="space-y-3">
      {rules.map((rule) => (
        <div key={rule.code} className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {/* Rule Header */}
          <div 
            className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
            onClick={() => toggleExpanded(rule.code)}
          >
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {expandedRules.has(rule.code) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-800">{rule.name}</span>
                    <code className="text-xs font-mono bg-slate-100 px-1.5 py-0.5 rounded">{rule.code}</code>
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Triggers on: <span className="font-medium">{getIntentName(rule.trigger?.value)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-4" onClick={(e) => e.stopPropagation()}>
              {/* Counts */}
              <div className="flex items-center gap-3 text-xs">
                {(rule.disturbances?.length || 0) > 0 && (
                  <span className="flex items-center gap-1 text-amber-600">
                    <AlertTriangle size={12} />
                    {rule.disturbances.length}
                  </span>
                )}
                {(rule.suggested_additions?.length || 0) > 0 && (
                  <span className="flex items-center gap-1 text-green-600">
                    <Lightbulb size={12} />
                    {rule.suggested_additions.length}
                  </span>
                )}
                {(rule.risks?.length || 0) > 0 && (
                  <span className="flex items-center gap-1 text-red-600">
                    <ShieldAlert size={12} />
                    {rule.risks.length}
                  </span>
                )}
              </div>
              
              {/* Priority */}
              <span className="text-xs text-slate-500">P{rule.priority || 100}</span>
              
              {/* Status Toggle */}
              <button
                onClick={() => onToggle(rule.code)}
                className={`p-1.5 rounded transition-colors ${rule.is_active ? 'text-green-600 hover:bg-green-50' : 'text-slate-400 hover:bg-slate-100'}`}
                title={rule.is_active ? 'Active - Click to deactivate' : 'Inactive - Click to activate'}
              >
                {rule.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
              </button>
              
              {/* Actions */}
              <button onClick={() => onEdit(rule)} className="p-1.5 hover:bg-slate-100 rounded" title="Edit">
                <Edit2 size={14} className="text-slate-500" />
              </button>
              <button onClick={() => onDelete(rule.code)} className="p-1.5 hover:bg-red-50 rounded" title="Delete">
                <Trash2 size={14} className="text-red-500" />
              </button>
            </div>
          </div>
          
          {/* Expanded Content */}
          {expandedRules.has(rule.code) && (
            <div className="px-4 pb-4 border-t border-slate-100 bg-slate-50">
              <div className="grid grid-cols-3 gap-4 mt-3">
                {/* Disturbances */}
                <div>
                  <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <AlertTriangle size={12} />
                    Disturbances ({rule.disturbances?.length || 0})
                  </h4>
                  <div className="space-y-1">
                    {(rule.disturbances || []).map((d, i) => (
                      <div key={i} className="text-xs bg-white rounded px-2 py-1.5 border border-amber-100">
                        <div className="font-medium text-slate-700">{d.label}</div>
                        <div className="text-slate-400 text-[10px]">{d.code} • {d.confidence}</div>
                      </div>
                    ))}
                    {(!rule.disturbances || rule.disturbances.length === 0) && (
                      <div className="text-xs text-slate-400 italic">No disturbances defined</div>
                    )}
                  </div>
                </div>
                
                {/* Suggested Additions */}
                <div>
                  <h4 className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <Lightbulb size={12} />
                    Suggestions ({rule.suggested_additions?.length || 0})
                  </h4>
                  <div className="space-y-1">
                    {(rule.suggested_additions || []).map((s, i) => (
                      <div key={i} className="text-xs bg-white rounded px-2 py-1.5 border border-green-100">
                        <div className="font-medium text-slate-700">{s.label}</div>
                        <div className="text-slate-400 text-[10px]">
                          {s.type} • {s.confidence}
                          {s.auto_add && <span className="ml-1 text-green-600">• Auto-add</span>}
                        </div>
                      </div>
                    ))}
                    {(!rule.suggested_additions || rule.suggested_additions.length === 0) && (
                      <div className="text-xs text-slate-400 italic">No suggestions defined</div>
                    )}
                  </div>
                </div>
                
                {/* Risks */}
                <div>
                  <h4 className="text-xs font-semibold text-red-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                    <ShieldAlert size={12} />
                    Risks ({rule.risks?.length || 0})
                  </h4>
                  <div className="space-y-1">
                    {(rule.risks || []).map((r, i) => (
                      <div key={i} className="text-xs bg-white rounded px-2 py-1.5 border border-red-100">
                        <div className="font-medium text-slate-700">{r.label}</div>
                        <div className="text-slate-400 text-[10px]">{r.code} • {r.confidence}</div>
                        {r.mitigation && <div className="text-slate-500 text-[10px] mt-0.5">Mitigation: {r.mitigation}</div>}
                      </div>
                    ))}
                    {(!rule.risks || rule.risks.length === 0) && (
                      <div className="text-xs text-slate-400 italic">No risks defined</div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Guidance */}
              {((rule.foh_pricing_guidance?.length || 0) > 0 || (rule.customer_explanation_guidance?.length || 0) > 0) && (
                <div className="mt-4 pt-3 border-t border-slate-200">
                  <div className="grid grid-cols-2 gap-4">
                    {(rule.foh_pricing_guidance?.length || 0) > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-slate-600 mb-1">FOH Pricing Guidance</h4>
                        <ul className="text-xs text-slate-600 space-y-0.5">
                          {rule.foh_pricing_guidance.map((g, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <span className="text-indigo-400">•</span> {g}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(rule.customer_explanation_guidance?.length || 0) > 0 && (
                      <div>
                        <h4 className="text-xs font-semibold text-slate-600 mb-1">Customer Explanation</h4>
                        <ul className="text-xs text-slate-600 space-y-0.5">
                          {rule.customer_explanation_guidance.map((g, i) => (
                            <li key={i} className="flex items-start gap-1">
                              <span className="text-indigo-400">•</span> {g}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ============== REPAIR INTENT MODAL ==============

function RepairIntentModal({ intent, onClose, onSave, systems }) {
  const [formData, setFormData] = useState({
    code: intent?.code || '',
    name: intent?.name || '',
    description: intent?.description || '',
    system: intent?.system || 'general',
    is_active: intent?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code || !formData.name) {
      toast.error('Code and Name are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800">
            {intent ? 'Edit Repair Intent' : 'New Repair Intent'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={20} className="text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Code *</label>
            <input
              type="text"
              value={formData.code}
              onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s+/g, '_') }))}
              disabled={!!intent}
              placeholder="e.g., BRAKE_FRONT_OVERHAUL"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="e.g., Front Brake Overhaul"
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description..."
              rows={2}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">System</label>
            <select
              value={formData.system}
              onChange={(e) => setFormData(prev => ({ ...prev, system: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {systems.map(s => (
                <option key={s.id} value={s.id}>{s.label}</option>
              ))}
            </select>
          </div>
          
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
              className="rounded border-slate-300"
            />
            <label htmlFor="is_active" className="text-sm text-slate-700">Active</label>
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              {intent ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============== JCE RULE MODAL ==============

function JCERuleModal({ rule, onClose, onSave, repairIntents }) {
  const [formData, setFormData] = useState({
    code: rule?.code || '',
    name: rule?.name || '',
    description: rule?.description || '',
    is_active: rule?.is_active ?? true,
    priority: rule?.priority || 100,
    trigger: rule?.trigger || { type: 'repair_intent_code', operator: 'equals', value: '' },
    disturbances: rule?.disturbances || [],
    suggested_additions: rule?.suggested_additions || [],
    risks: rule?.risks || [],
    foh_pricing_guidance: rule?.foh_pricing_guidance || [],
    customer_explanation_guidance: rule?.customer_explanation_guidance || [],
  });
  const [saving, setSaving] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.code || !formData.name || !formData.trigger.value) {
      toast.error('Code, Name, and Trigger Intent are required');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  // Add/Remove array items
  const addDisturbance = () => {
    setFormData(prev => ({
      ...prev,
      disturbances: [...prev.disturbances, { code: '', label: '', confidence: 'high' }]
    }));
  };

  const removeDisturbance = (index) => {
    setFormData(prev => ({
      ...prev,
      disturbances: prev.disturbances.filter((_, i) => i !== index)
    }));
  };

  const updateDisturbance = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      disturbances: prev.disturbances.map((d, i) => i === index ? { ...d, [field]: value } : d)
    }));
  };

  const addSuggestion = () => {
    setFormData(prev => ({
      ...prev,
      suggested_additions: [...prev.suggested_additions, { 
        type: 'labour', code: '', label: '', confidence: 'high', auto_add: false, reason: '' 
      }]
    }));
  };

  const removeSuggestion = (index) => {
    setFormData(prev => ({
      ...prev,
      suggested_additions: prev.suggested_additions.filter((_, i) => i !== index)
    }));
  };

  const updateSuggestion = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      suggested_additions: prev.suggested_additions.map((s, i) => i === index ? { ...s, [field]: value } : s)
    }));
  };

  const addRisk = () => {
    setFormData(prev => ({
      ...prev,
      risks: [...prev.risks, { code: '', label: '', confidence: 'high', mitigation: '' }]
    }));
  };

  const removeRisk = (index) => {
    setFormData(prev => ({
      ...prev,
      risks: prev.risks.filter((_, i) => i !== index)
    }));
  };

  const updateRisk = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      risks: prev.risks.map((r, i) => i === index ? { ...r, [field]: value } : r)
    }));
  };

  const addGuidance = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeGuidance = (field, index) => {
    setFormData(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  const updateGuidance = (field, index, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((g, i) => i === index ? value : g)
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-lg font-semibold text-slate-800">
            {rule ? 'Edit JCE Rule' : 'New JCE Rule'}
          </h2>
          <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded">
            <X size={20} className="text-slate-500" />
          </button>
        </div>
        
        {/* Section Tabs */}
        <div className="px-6 border-b border-slate-200 flex gap-1 flex-shrink-0">
          {['basic', 'disturbances', 'suggestions', 'risks', 'guidance'].map(section => (
            <button
              key={section}
              onClick={() => setActiveSection(section)}
              className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeSection === section
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {section.charAt(0).toUpperCase() + section.slice(1)}
              {section === 'disturbances' && formData.disturbances.length > 0 && ` (${formData.disturbances.length})`}
              {section === 'suggestions' && formData.suggested_additions.length > 0 && ` (${formData.suggested_additions.length})`}
              {section === 'risks' && formData.risks.length > 0 && ` (${formData.risks.length})`}
            </button>
          ))}
        </div>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-4">
            {/* Basic Info */}
            {activeSection === 'basic' && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Code *</label>
                    <input
                      type="text"
                      value={formData.code}
                      onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value.toUpperCase().replace(/\s+/g, '_') }))}
                      disabled={!!rule}
                      placeholder="e.g., RULE_BRAKE_FRONT"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-50"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., Front Brake Job Guidance"
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="What this rule does..."
                    rows={2}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Trigger Repair Intent *</label>
                    <select
                      value={formData.trigger.value}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        trigger: { ...prev.trigger, value: e.target.value } 
                      }))}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">Select an intent...</option>
                      {repairIntents.filter(i => i.is_active).map(intent => (
                        <option key={intent.code} value={intent.code}>
                          {intent.name} ({intent.code})
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Priority</label>
                    <input
                      type="number"
                      value={formData.priority}
                      onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 100 }))}
                      min={1}
                      max={999}
                      className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                    <p className="text-xs text-slate-500 mt-1">Higher = more important</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="rule_is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="rounded border-slate-300"
                  />
                  <label htmlFor="rule_is_active" className="text-sm text-slate-700">Active</label>
                </div>
              </>
            )}
            
            {/* Disturbances */}
            {activeSection === 'disturbances' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">Items that will be disturbed by this repair</p>
                  <button type="button" onClick={addDisturbance} className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
                    <Plus size={14} /> Add Disturbance
                  </button>
                </div>
                {formData.disturbances.map((d, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                    <div className="flex-1 grid grid-cols-3 gap-3">
                      <input
                        type="text"
                        value={d.code}
                        onChange={(e) => updateDisturbance(i, 'code', e.target.value)}
                        placeholder="Code (e.g., FRONT_CALIPER)"
                        className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                      />
                      <input
                        type="text"
                        value={d.label}
                        onChange={(e) => updateDisturbance(i, 'label', e.target.value)}
                        placeholder="Label"
                        className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                      />
                      <select
                        value={d.confidence}
                        onChange={(e) => updateDisturbance(i, 'confidence', e.target.value)}
                        className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                      >
                        {CONFIDENCE_LEVELS.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <button type="button" onClick={() => removeDisturbance(i)} className="p-1 hover:bg-red-100 rounded">
                      <X size={14} className="text-red-500" />
                    </button>
                  </div>
                ))}
                {formData.disturbances.length === 0 && (
                  <p className="text-sm text-slate-400 italic text-center py-4">No disturbances added</p>
                )}
              </div>
            )}
            
            {/* Suggestions */}
            {activeSection === 'suggestions' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">Additional items to suggest with this repair</p>
                  <button type="button" onClick={addSuggestion} className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
                    <Plus size={14} /> Add Suggestion
                  </button>
                </div>
                {formData.suggested_additions.map((s, i) => (
                  <div key={i} className="p-3 bg-green-50 rounded-lg border border-green-100 space-y-2">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 grid grid-cols-4 gap-2">
                        <select
                          value={s.type}
                          onChange={(e) => updateSuggestion(i, 'type', e.target.value)}
                          className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                        >
                          {ADDITION_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                        <input
                          type="text"
                          value={s.code}
                          onChange={(e) => updateSuggestion(i, 'code', e.target.value)}
                          placeholder="Code"
                          className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                        />
                        <input
                          type="text"
                          value={s.label}
                          onChange={(e) => updateSuggestion(i, 'label', e.target.value)}
                          placeholder="Label"
                          className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                        />
                        <select
                          value={s.confidence}
                          onChange={(e) => updateSuggestion(i, 'confidence', e.target.value)}
                          className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                        >
                          {CONFIDENCE_LEVELS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <button type="button" onClick={() => removeSuggestion(i)} className="p-1 hover:bg-red-100 rounded">
                        <X size={14} className="text-red-500" />
                      </button>
                    </div>
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-1 text-xs text-slate-600">
                        <input
                          type="checkbox"
                          checked={s.auto_add}
                          onChange={(e) => updateSuggestion(i, 'auto_add', e.target.checked)}
                          className="rounded border-slate-300"
                        />
                        Auto-add
                      </label>
                      <input
                        type="text"
                        value={s.reason || ''}
                        onChange={(e) => updateSuggestion(i, 'reason', e.target.value)}
                        placeholder="Reason for suggestion"
                        className="flex-1 px-2 py-1 border border-slate-200 rounded text-xs"
                      />
                    </div>
                  </div>
                ))}
                {formData.suggested_additions.length === 0 && (
                  <p className="text-sm text-slate-400 italic text-center py-4">No suggestions added</p>
                )}
              </div>
            )}
            
            {/* Risks */}
            {activeSection === 'risks' && (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-600">Risks to communicate to customer</p>
                  <button type="button" onClick={addRisk} className="text-sm text-indigo-600 hover:underline flex items-center gap-1">
                    <Plus size={14} /> Add Risk
                  </button>
                </div>
                {formData.risks.map((r, i) => (
                  <div key={i} className="p-3 bg-red-50 rounded-lg border border-red-100 space-y-2">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 grid grid-cols-3 gap-2">
                        <input
                          type="text"
                          value={r.code}
                          onChange={(e) => updateRisk(i, 'code', e.target.value)}
                          placeholder="Code"
                          className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                        />
                        <input
                          type="text"
                          value={r.label}
                          onChange={(e) => updateRisk(i, 'label', e.target.value)}
                          placeholder="Risk label"
                          className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                        />
                        <select
                          value={r.confidence}
                          onChange={(e) => updateRisk(i, 'confidence', e.target.value)}
                          className="px-2 py-1.5 border border-slate-200 rounded text-sm"
                        >
                          {CONFIDENCE_LEVELS.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      <button type="button" onClick={() => removeRisk(i)} className="p-1 hover:bg-red-100 rounded">
                        <X size={14} className="text-red-500" />
                      </button>
                    </div>
                    <input
                      type="text"
                      value={r.mitigation || ''}
                      onChange={(e) => updateRisk(i, 'mitigation', e.target.value)}
                      placeholder="Mitigation strategy"
                      className="w-full px-2 py-1 border border-slate-200 rounded text-xs"
                    />
                  </div>
                ))}
                {formData.risks.length === 0 && (
                  <p className="text-sm text-slate-400 italic text-center py-4">No risks added</p>
                )}
              </div>
            )}
            
            {/* Guidance */}
            {activeSection === 'guidance' && (
              <div className="space-y-6">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">FOH Pricing Guidance</label>
                    <button type="button" onClick={() => addGuidance('foh_pricing_guidance')} className="text-xs text-indigo-600 hover:underline">
                      + Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.foh_pricing_guidance.map((g, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={g}
                          onChange={(e) => updateGuidance('foh_pricing_guidance', i, e.target.value)}
                          placeholder="Guidance point..."
                          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        />
                        <button type="button" onClick={() => removeGuidance('foh_pricing_guidance', i)} className="p-1 hover:bg-red-100 rounded">
                          <X size={14} className="text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="text-sm font-medium text-slate-700">Customer Explanation Guidance</label>
                    <button type="button" onClick={() => addGuidance('customer_explanation_guidance')} className="text-xs text-indigo-600 hover:underline">
                      + Add
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.customer_explanation_guidance.map((g, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={g}
                          onChange={(e) => updateGuidance('customer_explanation_guidance', i, e.target.value)}
                          placeholder="Guidance point..."
                          className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-sm"
                        />
                        <button type="button" onClick={() => removeGuidance('customer_explanation_guidance', i)} className="p-1 hover:bg-red-100 rounded">
                          <X size={14} className="text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-3 flex-shrink-0 bg-slate-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
            >
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Check size={14} />}
              {rule ? 'Update Rule' : 'Create Rule'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
